import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Card,
  DatePicker,
  Segmented,
  Statistic,
  Button,
  Space,
  Typography,
  Spin,
} from "antd";
import {
  FileOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  ApartmentOutlined,
  BranchesOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { mcapLogsService } from "../services/mcapLogsService";
import { ROBOT_LOGS_LIST_PATH } from "../utils/logConstants";
import { buildSearch, parseWindowQuery, windowFilters } from "../utils/logQuery";
import {
  WINDOW_PICKER_PROPS,
  boundsToWindow,
  buildWindowPresets,
  windowToBounds,
} from "../utils/windowPicker";
import { bucketBounds, rollUp, uploadsChartRange } from "../utils/timeBuckets";
import EPieChart from "shared/components/ui/chart/EPieChart";
import EBarChart from "shared/components/ui/chart/EBarChart";
import { BUTTON_STYLES } from "features/administration/constants";

const { Title } = Typography;
const { RangePicker } = DatePicker;

// Trailing days the uploads chart covers when no window bounds it (also the
// server's `days` fallback).
const DEFAULT_CHART_DAYS = 30;

// Title of the uploads chart per bucket granularity.
const UPLOADS_CHART_TITLES = {
  day: "mcapLogs.dashboard.charts.dailyUploads",
  week: "mcapLogs.dashboard.charts.weeklyUploads",
  month: "mcapLogs.dashboard.charts.monthlyUploads",
};

// ISO-8601 UTC instants of one format compare as strings.
const laterOf = (a, b) => (a && b ? (a > b ? a : b) : a || b);
const earlierOf = (a, b) => (a && b ? (a < b ? a : b) : a || b);

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

// ─── Client-side aggregation helpers ─────────────────────────────────────────

const topN = (obj, n = 10) =>
  Object.entries(obj)
    .sort(([, a], [, b]) => b - a)
    .slice(0, n)
    .map(([name, value]) => ({ name, value }));

const toPieData = (obj) =>
  Object.entries(obj).map(([name, value]) => ({ name, value }));

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ title, value, prefix, icon, color, suffix, loading }) => (
  <Card
    style={{ borderTop: `3px solid ${color}` }}
    styles={{ body: { padding: "8px 12px" } }}
  >
    <Spin spinning={loading}>
      <Space align="start" size={8}>
        <div
          style={{
            background: `${color}18`,
            borderRadius: 6,
            padding: "6px 8px",
            color,
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          {icon}
        </div>
        <Statistic
          title={<span style={{ fontSize: 11 }}>{title}</span>}
          value={value}
          prefix={prefix}
          suffix={suffix}
          valueStyle={{ fontSize: 16, fontWeight: 600 }}
        />
      </Space>
    </Spin>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const McapLogsDashboard = () => {
  const { t } = useTranslation();
  const { authFetch, isAuthenticated } = useAuth();
  const { notifyError } = useNotification();
  const { selectedOrg } = useUserOrg();
  const history = useHistory();
  const location = useLocation();

  const orgId = selectedOrg?.organization?.org_id ?? null;

  // The statistics window lives in the URL (shareable, survives reload) like
  // the list page's filters: `field` says whether it bounds upload or
  // recording time, and no bounds means all time.
  const timeWindow = useMemo(() => parseWindowQuery(location.search), [location.search]);
  const windowQuery = useMemo(() => windowFilters(timeWindow), [timeWindow]);
  // The timeline toggle is kept locally so it can be switched before a range
  // is picked; the URL carries it only together with a range.
  const [field, setField] = useState(timeWindow.field);
  useEffect(() => {
    if (timeWindow.from || timeWindow.to) setField(timeWindow.field);
  }, [timeWindow]);
  const windowPresets = useMemo(() => buildWindowPresets(t), [t]);

  const [stats, setStats] = useState(null);
  // `range` is the uploads chart's axis for the data it was loaded with.
  const [timeseries, setTimeseries] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [timeseriesLoading, setTimeseriesLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;

    setStatsLoading(true);
    setTimeseriesLoading(true);

    // The uploads chart always runs on upload time (see uploadsChartRange for
    // how the window maps onto it); its lower bound is sent explicitly so the
    // server and the axis agree on the first bucket.
    const range = uploadsChartRange(timeWindow, Date.now(), DEFAULT_CHART_DAYS);
    const firstBucket =
      range.keys.length > 0 ? bucketBounds(range.keys[0], range.mode) : undefined;
    try {
      const [statsRes, timeseriesRes] = await Promise.all([
        mcapLogsService.getStats(authFetch, { org_id: orgId, ...windowQuery }),
        mcapLogsService.getTimeseries(authFetch, {
          org_id: orgId,
          days: DEFAULT_CHART_DAYS,
          ...windowQuery,
          uploaded_from: timeWindow.from ?? firstBucket?.from,
        }),
      ]);
      setStats(statsRes.data);
      setTimeseries({ range, data: timeseriesRes.data });
    } catch (err) {
      notifyError(t("mcapLogs.dashboard.loadError") + (err.message || err));
    } finally {
      setStatsLoading(false);
      setTimeseriesLoading(false);
    }
  }, [authFetch, isAuthenticated, notifyError, orgId, timeWindow, windowQuery]);

  useEffect(() => {
    loadData();
  }, [loadData, selectedOrg]);

  // Every chart item is a link into the list page with the matching filter
  // and the current window pre-applied; the list reads its filters from the
  // URL, so the link is also shareable. Stats and list are scoped to the
  // same organisation, so the counts line up.
  const openList = (filters) =>
    history.push({
      pathname: ROBOT_LOGS_LIST_PATH,
      search: buildSearch({ ...windowQuery, ...filters }),
    });
  const openListForBucket = (key) => {
    const range = timeseries?.range;
    const bounds = range && bucketBounds(key, range.mode);
    if (!bounds) return;
    // Clipped to an upload window so the list shows exactly the bar's files.
    const clip = timeWindow.field === "uploaded" ? timeWindow : {};
    openList({
      uploaded_from: laterOf(bounds.from, clip.from),
      uploaded_to: earlierOf(bounds.to, clip.to),
    });
  };

  const applyWindow = (nextField, { from, to }) =>
    history.push({ search: buildSearch(windowFilters({ field: nextField, from, to })) });
  const handleWindowChange = (dates) => applyWindow(field, windowToBounds(dates));
  const handleFieldChange = (nextField) => {
    setField(nextField);
    if (timeWindow.from || timeWindow.to) applyWindow(nextField, timeWindow);
  };

  // ── Derived chart data ────────────────────────────────────────────────────

  const uploadStatePie = toPieData(stats?.files_by_upload_state ?? {});
  const processingStatePie = toPieData(stats?.files_by_processing_state ?? {});
  const recordingStatePie = toPieData(stats?.files_by_recording_state ?? {});

  const byRobotPie = topN(stats?.files_by_robot ?? {});
  const byTaskPie = topN(stats?.files_by_task ?? {});
  const bySceneTypePie = topN(stats?.files_by_scene_type ?? {});
  const byLocationPie = topN(stats?.files_by_location ?? {});

  // The server aggregates daily_uploads/daily_storage per UTC day; they are
  // rolled up into the chart's day/week/month buckets, missing ones as 0.
  const range = timeseries?.range;
  const chartKeys = range?.keys ?? [];
  const uploadsCounts = rollUp(timeseries?.data?.daily_uploads, chartKeys, range?.mode);
  const uploadsStorageMb = rollUp(timeseries?.data?.daily_storage, chartKeys, range?.mode).map(
    (bytes) => +(bytes / (1024 * 1024)).toFixed(2)
  );
  const uploadsSeries = [
    {
      name: t("mcapLogs.dashboard.charts.filesUploaded"),
      type: "bar",
      data: uploadsCounts,
      itemStyle: { color: "#1677ff" },
      yAxisIndex: 0,
    },
    {
      name: t("mcapLogs.dashboard.charts.storageMb"),
      type: "bar",
      data: uploadsStorageMb,
      itemStyle: { color: "#52c41a" },
      smooth: true,
      yAxisIndex: 1,
    },
  ];

  const topRobots = Object.entries(stats?.storage_by_robot ?? {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
  const storageSeries = [
    {
      name: t("mcapLogs.dashboard.charts.storageMb"),
      type: "bar",
      data: topRobots.map(([, v]) => +(v / (1024 * 1024)).toFixed(2)),
      itemStyle: { color: "#52c41a" },
    },
  ];

  const loading = statsLoading || timeseriesLoading;

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "8px",
        gap: 6,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ flexShrink: 0 }}>
        <Col>
          <Space size={6}>
            <BarChartOutlined style={{ fontSize: 16 }} />
            <Title level={5} style={{ margin: 0 }}>
              {t("mcapLogs.dashboard.title")}
            </Title>
            {selectedOrg && (
              <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                — {selectedOrg.organization?.name ?? selectedOrg.organization?.org_id}
              </Typography.Text>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {t("mcapLogs.dashboard.clickHint")}
            </Typography.Text>
          </Space>
        </Col>
        <Col>
          <Space size={6} wrap>
            {/* Statistics window: which timeline it bounds, and the bounds. */}
            <Segmented
              size="small"
              value={field}
              onChange={handleFieldChange}
              options={[
                { label: t("mcapLogs.dashboard.window.uploaded"), value: "uploaded" },
                { label: t("mcapLogs.dashboard.window.recorded"), value: "recorded" },
              ]}
            />
            <RangePicker
              {...WINDOW_PICKER_PROPS}
              size="small"
              presets={windowPresets}
              placeholder={[
                t("mcapLogs.dashboard.window.from"),
                t("mcapLogs.dashboard.window.to"),
              ]}
              value={boundsToWindow(timeWindow.from, timeWindow.to)}
              onChange={handleWindowChange}
            />
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              style={BUTTON_STYLES.add}
              onClick={() => openList({})}
            >
              {t("mcapLogs.dashboard.viewAllLogs")}
            </Button>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              style={BUTTON_STYLES.add}
              onClick={loadData}
              loading={loading}
            >
              {t("mcapLogs.dashboard.refresh")}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* ── KPI Cards ── */}
      <Row gutter={[8, 0]} style={{ flexShrink: 0 }} wrap={false}>
        {[
          { title: t("mcapLogs.dashboard.kpi.totalFiles"), value: stats?.total_files ?? 0, icon: <FileOutlined />, color: "#1677ff", isLoading: statsLoading },
          { title: t("mcapLogs.dashboard.kpi.totalStorage"), value: formatBytes(stats?.total_size_bytes), icon: <DatabaseOutlined />, color: "#52c41a", isLoading: statsLoading },
          { title: t("mcapLogs.dashboard.kpi.totalDuration"), value: formatDuration(stats?.total_duration_seconds), icon: <ClockCircleOutlined />, color: "#fa8c16", isLoading: statsLoading },
          { title: t("mcapLogs.dashboard.kpi.totalMessages"), value: stats?.total_messages?.toLocaleString() ?? 0, icon: <MessageOutlined />, color: "#722ed1", isLoading: statsLoading },
          { title: t("mcapLogs.dashboard.kpi.uniqueSchemas"), value: stats?.unique_schema_count ?? 0, icon: <ApartmentOutlined />, color: "#13c2c2", isLoading: statsLoading },
          { title: t("mcapLogs.dashboard.kpi.uniqueTopics"), value: stats?.unique_topic_count ?? 0, icon: <BranchesOutlined />, color: "#eb2f96", isLoading: statsLoading },
        ].map(({ title, value, icon, color, isLoading }) => (
          <Col flex="1" key={title}>
            <KpiCard title={title} value={value} icon={icon} color={color} loading={isLoading} />
          </Col>
        ))}
      </Row>

      {/* ── Pie Charts (5 in one row) ── */}
      <div style={{ display: "flex", gap: 6, flex: "1 1 0", minHeight: 0 }}>
        {[
          { title: t("mcapLogs.dashboard.charts.uploadState"), data: uploadStatePie, isLoading: statsLoading, filterKey: "upload_state" },
          { title: t("mcapLogs.dashboard.charts.processingState"), data: processingStatePie, isLoading: statsLoading, filterKey: "processing_state" },
          { title: t("mcapLogs.dashboard.charts.recordingState"), data: recordingStatePie, isLoading: statsLoading, filterKey: "recording_state" },
          { title: t("mcapLogs.dashboard.charts.byRobot"), data: byRobotPie, isLoading: statsLoading, filterKey: "robot_id" },
          { title: t("mcapLogs.dashboard.charts.byLocation"), data: byLocationPie, isLoading: statsLoading, filterKey: "location" },
        ].map(({ title, data, isLoading, filterKey }) => (
          <Card
            key={title}
            variant="borderless"
            className="criclebox"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            styles={{ body: { flex: 1, padding: "4px 6px", minHeight: 0, overflow: "hidden", position: "relative" } }}
          >
            {isLoading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 10 }}>
                <Spin />
              </div>
            )}
            <EPieChart
              data={data}
              title={title}
              style={{ height: "100%" }}
              onItemClick={(item) => openList({ [filterKey]: item.name })}
            />
          </Card>
        ))}
      </div>

      {/* ── Bar Charts (4 in one row) ── */}
      <div style={{ display: "flex", gap: 6, flex: "1 1 0", minHeight: 0 }}>
        {[
          {
            title: t("mcapLogs.dashboard.charts.filesPerTask"),
            categories: byTaskPie.map((d) => d.name),
            series: [{ name: t("mcapLogs.dashboard.charts.files"), type: "bar", data: byTaskPie.map((d) => d.value), itemStyle: { color: "#fa8c16" } }],
            isLoading: statsLoading,
            onItemClick: (item) => openList({ tasks: [item.name] }),
            flex: "1 1 0",
          },
          {
            title: t("mcapLogs.dashboard.charts.filesPerSceneType"),
            categories: bySceneTypePie.map((d) => d.name),
            series: [{ name: t("mcapLogs.dashboard.charts.files"), type: "bar", data: bySceneTypePie.map((d) => d.value), itemStyle: { color: "#13c2c2" } }],
            isLoading: statsLoading,
            onItemClick: (item) => openList({ scene_types: [item.name] }),
            flex: "1 1 0",
          },
          {
            title: t(UPLOADS_CHART_TITLES[range?.mode ?? "day"]),
            categories: chartKeys,
            series: uploadsSeries,
            yAxis: [
              { type: "value", name: t("mcapLogs.dashboard.charts.files") },
              { type: "value", name: "Size", axisLabel: { formatter: "{value} MB" } },
            ],
            legend: { show: true, top: 35 },
            isLoading: timeseriesLoading,
            // Both series (files, MB) sit on the same bucket category.
            onItemClick: (item) => openListForBucket(item.name),
            flex: "2 1 0",
          },
          {
            title: t("mcapLogs.dashboard.charts.storagePerRobot"),
            categories: topRobots.map(([id]) => id),
            series: storageSeries,
            isLoading: statsLoading,
            onItemClick: (item) => openList({ robot_id: item.name }),
            flex: "1 1 0",
          },
        ].map(({ title, categories, series, yAxis, legend, isLoading, flex: flexVal, onItemClick }) => (
          <Card
            key={title}
            variant="borderless"
            className="criclebox"
            style={{
              flex: flexVal,
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            styles={{ body: { flex: 1, padding: "4px 6px", minHeight: 0, overflow: "hidden", position: "relative" } }}
          >
            {isLoading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 10 }}>
                <Spin />
              </div>
            )}
            <EBarChart
              categories={categories}
              series={series}
              title={title}
              yAxis={yAxis}
              legend={legend}
              style={{ height: "100%" }}
              onItemClick={onItemClick}
            />
          </Card>
        ))}
      </div>
    </div>
  );
};

export default McapLogsDashboard;
